import React, { useState, useMemo } from 'react';
import { Form, Button, Modal } from 'react-bootstrap';
import { CircularProgress } from '@mui/material';
import CustomTable from '../Table/CustomTable';
import { httpsCallable, getFunctions } from 'firebase/functions';
import firebaseApp from '../../util/firebase';
import {
  updateConfiguration,
  useFindChartGroupConfigurationByShortKey,
  getControlStrategyConfigurationByFullKey,
  getSampleSchedulesByShortKey,
  getScheduleSamplesByFullKey,
  deleteScheduledSample,
  getEntitiesByShortKey,
  updateSampleProperty,
  deleteConfig
} from '../../util/db';
import { useHistory } from 'react-router-dom/cjs/react-router-dom.min';
const functions = getFunctions(firebaseApp);

// spc* variants: the unprefixed functions are owned by InsightSQC in shared
// projects and reject variation="spc" post-split. This modal is only used
// from the SPC page in this app.
const addMultipleDataFunction = httpsCallable(functions, 'spcAddMultipleData', {
  timeout: 540000
});

const runMultipleDataFunction = httpsCallable(functions, 'spcRunMultipleData', {
  timeout: 540000
});
const runData = httpsCallable(functions, 'runData', { timeout: 540000 });
const spcRunData = httpsCallable(functions, 'spcRunData', { timeout: 540000 });

const ArchiveRestartModal = ({
  isShowModal,
  setShowModal,
  filteredChartHits,
  columns,
  runArchiveDataFunction,
  archiveDataFunction,
  currentShortKey,
  currentFullKey,
  currentParameter,
  checkedCombinedInitialAssessment,
  currentControlStrategyConfigurations,
  currentChartGroupConfigurations,
  setChartLoading,
  spcArchiveDataFunction,
  spcRunArchiveDataFunction
}) => {
  const [loading, setLoading] = useState(false);
  const [keepRowIds, setKeepRowIds] = useState(new Set());
  const defaultCombinedParameterName = 'Combined Z-score';

  const keepRows = useMemo(
    () => filteredChartHits.filter((row) => keepRowIds.has(row.id)),
    [filteredChartHits, keepRowIds]
  );

  const history = useHistory();
  const isSpcPage = history.location.pathname.includes('/spc');
  const runDataFunction = isSpcPage ? spcRunData : runData;
  const variation = isSpcPage ? 'spc' : '';
  const chartGroupConfig = useFindChartGroupConfigurationByShortKey(
    currentChartGroupConfigurations?.shortKey,
    isSpcPage ? 'spc' : ''
  );
  const getRunDataParams = (keyParts, isSpcPage) =>
    isSpcPage
      ? {
          location: keyParts[0],
          type: keyParts[1],
          product: keyParts[2],
          method: keyParts[3],
          parameter: keyParts[4]
        }
      : {
          location: keyParts[0],
          method: keyParts[1],
          sampleName: keyParts[2],
          instrument: keyParts[3],
          parameter: keyParts[4]
        };

  const extractSplitData = (splitData, isSpcPage) =>
    isSpcPage
      ? {
          archivedLocation: splitData[0],
          archivedType: splitData[1],
          archivedProduct: splitData[2],
          archivedMethod: splitData[3],
          archivedParameter: splitData[4]
        }
      : {
          archivedLocation: splitData[0],
          archivedMethod: splitData[1],
          archivedSampleName: splitData[2],
          archivedInstrument: splitData[3],
          archivedParameter: splitData[4]
        };

  const saveParsedData = async (rows, options = {}) => {
    const { skipRunMultiple = false } = options;
    const isCellBased = rows.length > 0 && rows[0].row;
    const extractedRows = isCellBased
      ? Array.from(new Map(rows.map((cell) => [cell.row.id, cell.row])).values())
      : rows;

    try {
      const addDataObject = {
        rows: extractedRows,
        variation: isSpcPage ? 'spc' : '',
        isImportFromFile: true
      };

      const response = await addMultipleDataFunction(addDataObject);
      if (response.data?.status !== 200) return;

      const runDataObject = {
        variation,
        listParameter: extractedRows.map((r) => ({
          location: r.location,
          method: r.method,
          instrument: r.instrument,
          sampleName: r.sampleName
        }))
      };

      if (!skipRunMultiple) {
        const runRes = await runMultipleDataFunction(runDataObject);
        if (runRes.data?.status !== 200 || extractedRows.length === 0) return;

        const firstRow = extractedRows[0];
        const keyParts = [
          firstRow.location,
          firstRow.type || '',
          firstRow.product || '',
          firstRow.method,
          firstRow.parameter
        ];
        const runDataParams = getRunDataParams(keyParts, isSpcPage);
        await runDataFunction(runDataParams);
      }
    } catch (e) {
      console.error('saveParsedData failed:', e);
    }
  };

  const columnsWithKeep = useMemo(() => {
    const keepColumn = {
      field: 'keep',
      headerName: 'Keep',
      width: 58,
      renderCell: (row) => {
        const isChecked = keepRowIds.has(row.id);
        return (
          <div className="d-flex justify-content-center align-items-center w-100 h-100">
            <Form.Check
              type="checkbox"
              checked={isChecked}
              onChange={(e) => {
                setKeepRowIds((prev) => {
                  const newSet = new Set(prev);
                  e.target.checked ? newSet.add(row.id) : newSet.delete(row.id);
                  return newSet;
                });
              }}
            />
          </div>
        );
      }
    };
    return [keepColumn, ...columns];
  }, [columns, keepRowIds]);

  const handleArchiveRestart = async () => {
    setChartLoading(true);

    const storedControlConfig = { ...currentControlStrategyConfigurations };
    const storedChartGroupConfig = { ...currentChartGroupConfigurations };

    try {
      const archiveFunction = isSpcPage ? spcArchiveDataFunction : archiveDataFunction;
      const runArchiveFunction = isSpcPage ? spcRunArchiveDataFunction : runArchiveDataFunction;
      const response = await archiveFunction({
        shortKey: currentShortKey,
        fullKey: currentFullKey,
        parameter: currentParameter,
        checkedCombinedInitialAssessment,
        keepRowIds: Array.from(keepRowIds)
      });

      const splitData = response.data[0].split('|');
      const runDataArchiveObject = extractSplitData(splitData, isSpcPage);
      await runArchiveFunction(runDataArchiveObject);

      if (keepRows?.length > 0) {
        const controlConfig = await getControlStrategyConfigurationByFullKey(
          storedControlConfig?.fullKey,
          variation
        );

        if (controlConfig?.length > 1) {
          // Delete duplicate configs if multiple exist
          // Sort to keep the most recently created config
          controlConfig.sort((a, b) => {
            const aTime = a.createdAt?.seconds || 0;
            const bTime = b.createdAt?.seconds || 0;
            return bTime - aTime; // Most recent first
          });
          const duplicateItems = controlConfig.slice(1);
          try {
            await Promise.all(duplicateItems.map((item) => deleteConfig(item.id, variation)));
          } catch (error) {
            console.error('Failed to delete duplicate configs:', error);
            throw new Error('Configuration cleanup failed during archive operation');
          }
        }
        if (controlConfig?.length > 0) {
          await updateConfiguration(controlConfig[0].id, storedControlConfig, variation);
        }

        if (chartGroupConfig?.id) {
          await updateConfiguration(chartGroupConfig.id, storedChartGroupConfig, variation);
        }

        if (storedControlConfig?.fullKey) {
          const keyParts = storedControlConfig.fullKey.split('|');
          const runDataParams = getRunDataParams(keyParts, isSpcPage);
          console.log('Calling runDataFunction with params:', runDataParams);
          await runDataFunction(runDataParams);
        }
      } else {
        //Archive entire dataset: Stop generating new scheduled samples and delete existing schedules
        try {
          console.log('No rows to keep, deleting scheduled samples schedules and scheduled data.');
          console.log('isSpcPage:', isSpcPage ? 'spc' : '');

          console.log('Fetching sample schedules with shortKey:', currentShortKey);
          console.log('Fetching scheduled samples with fullKey:', currentFullKey);

          if (!currentShortKey?.trim() || !currentFullKey?.trim()) {
            throw new Error('Missing or invalid keys.');
          }

          const variation = isSpcPage ? 'spc' : '';

          const [sampleSchedules, scheduleSamples] = await Promise.all([
            getSampleSchedulesByShortKey(currentShortKey, variation),
            getScheduleSamplesByFullKey(currentFullKey, variation)
          ]);

          console.log(`Get ShortKey Items with shortKey: ${currentShortKey}`);
          const shortKeyItems = await getEntitiesByShortKey(currentShortKey, variation);

          console.log(`Get Parameters...`);
          const parameters = [
            ...new Set(
              shortKeyItems
                .filter((item) => item.parameter !== defaultCombinedParameterName)
                .map((i) => i.parameter)
            )
          ];

          console.log(`Parameters to update: ${parameters}`);
          await Promise.all(
            sampleSchedules.map((s) =>
              updateSampleProperty(s?.id, { parameters: parameters }, variation)
            )
          );

          console.log(`Deleting ${scheduleSamples?.length || 0} scheduled samples...`);
          await Promise.all(scheduleSamples.map((s) => deleteScheduledSample(s?.id, variation)));

          console.log('Archive completed successfully. Redirecting...');
          history.push(isSpcPage ? '/spc' : '/chart');
        } catch (err) {
          console.error(
            'Deleting scheduled samples schedules and scheduled data process failed:',
            err
          );
          throw err;
        }
      }
    } catch (error) {
      console.error('Archiving failed:', error);
    } finally {
      setShowModal(false);
      setChartLoading(false);
      window.location.reload();
    }
  };
  return (
    <Modal
      show={isShowModal}
      onHide={() => {
        setShowModal(false);
        setKeepRowIds(new Set());
      }}
      dialogClassName="wide-modal"
      centered
    >
      <Modal.Header closeButton>
        <Modal.Title>Archive/Restart Select Modal</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {!currentFullKey || currentFullKey === '||||' ? (
          <p>No data. Please select fullKey to archive or restart data</p>
        ) : (
          <>
            <Form.Check
              type="checkbox"
              label="Keep All"
              className="mb-2 ml-5"
              checked={keepRowIds.size === filteredChartHits.length}
              onChange={(e) => {
                setKeepRowIds(
                  e.target.checked ? new Set(filteredChartHits.map((row) => row.id)) : new Set()
                );
              }}
            />
            <CustomTable
              header={columnsWithKeep}
              data={filteredChartHits}
              numberOfRows={20}
              sort={{ sorting: { sortModel: [{ field: 'dateTime', sort: 'desc' }] } }}
            />
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button
          variant="primary"
          disabled={
            loading ||
            !currentFullKey ||
            currentFullKey === '||||' ||
            keepRowIds.size === filteredChartHits.length // disable if Keep All is checked
          }
          onClick={handleArchiveRestart}
        >
          {loading ? (
            <CircularProgress size={24} />
          ) : keepRowIds.size > 0 ? (
            'Keep selected data and Archive'
          ) : (
            'Archive'
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default ArchiveRestartModal;
